/**
 * ProjectDialog — Save / Load full workspace to GeoVera cloud (Postgres JSONB).
 * No local-only fallback: entire project must live in the cloud.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Delete, Save, CloudUpload, CloudDownload } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setMapView, setCurrentBasemap } from '../store/slices/mapSlice';
import { replaceAllLayers, type Layer } from '../store/slices/layersSlice';
import {
  clearAllDrawings,
  addDrawing,
  setDrawingStyle,
  setTextStyle,
} from '../store/slices/drawingSlice';
import { addNotification, setNonTechnicalMode } from '../store/slices/uiSlice';
import { normalizeFeaturesForMap } from '../utils/geojsonToVectorLayer';
import { mapNavigator } from '../services/MapNavigator';
import {
  buildWorkspaceSnapshot,
  listCloudProjects,
  saveProjectToCloud,
  loadProjectFromCloud,
  deleteCloudProject,
  getLastCloudProject,
  setLastCloudProject,
  type ProjectListItem,
  type ProjectStateSnapshot,
} from '../services/cloudProjectService';
import { canSaveToCloud } from '../services/cloudProjectService';
import { useNavigate } from 'react-router-dom';
import type { Drawing } from '../types/drawing';

interface ProjectDialogProps {
  open: boolean;
  mode: 'open' | 'save';
  onClose: () => void;
}

const ProjectDialog: React.FC<ProjectDialogProps> = ({ open, mode, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const layers = useAppSelector((s) => s.layers.layers);
  const activeLayerId = useAppSelector((s) => s.layers.activeLayerId);
  const mapView = useAppSelector((s) => s.map.view);
  const currentBasemap = useAppSelector((s) => s.map.currentBasemap);
  const drawings = useAppSelector((s) => s.drawing.drawings);
  const drawingStyle = useAppSelector((s) => s.drawing.drawingStyle);
  const textStyle = useAppSelector((s) => s.drawing.textStyle);
  const isNonTechnicalMode = useAppSelector((s) => s.ui.isNonTechnicalMode);

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const restoreViewTimerRef = useRef<number | null>(null);

  const buildSnapshot = useCallback((): ProjectStateSnapshot => {
    return buildWorkspaceSnapshot({
      layers,
      activeLayerId,
      mapView,
      basemap: currentBasemap,
      drawings,
      drawingStyle,
      textStyle,
      isNonTechnicalMode,
    });
  }, [
    layers,
    activeLayerId,
    mapView,
    currentBasemap,
    drawings,
    drawingStyle,
    textStyle,
    isNonTechnicalMode,
  ]);

  const applySnapshot = useCallback(
    (state: ProjectStateSnapshot) => {
      if (restoreViewTimerRef.current !== null) {
        window.clearTimeout(restoreViewTimerRef.current);
        restoreViewTimerRef.current = null;
      }

      const center = state.view?.center;
      const hasUsableSavedView =
        Array.isArray(center) &&
        center.length === 2 &&
        center.every(
          (coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate)
        ) &&
        center[0] >= -180 &&
        center[0] <= 180 &&
        center[1] >= -90 &&
        center[1] <= 90 &&
        typeof state.view?.zoom === 'number' &&
        Number.isFinite(state.view.zoom);
      const savedRotation =
        typeof state.view?.rotation === 'number' && Number.isFinite(state.view.rotation)
          ? state.view.rotation
          : 0;

      if (state.basemap) {
        dispatch(setCurrentBasemap(state.basemap));
      }
      if (Array.isArray(state.layers)) {
        const restored: Layer[] = state.layers.map((l, i) => {
          const layerId = l.id || `restored-${i}-${Date.now()}`;
          const features = normalizeFeaturesForMap(
            (l.features || []).map((f: any, fi: number) => ({
              id: f.id || `f-${i}-${fi}`,
              layerId,
              geometry: f.geometry,
              properties: f.properties || {},
            }))
          );
          return {
            id: layerId,
            name: l.name || `Layer ${i + 1}`,
            type: l.type === 'raster' ? 'raster' : 'vector',
            geometryType: l.geometryType,
            visible: l.visible !== false,
            opacity: typeof l.opacity === 'number' ? l.opacity : 1,
            zIndex: typeof l.zIndex === 'number' ? l.zIndex : i + 1,
            featureCount: features.length,
            features,
            symbology: l.symbology,
            crs: l.crs,
            source: l.source,
            sourceType: l.sourceType,
            sourceUrl: l.sourceUrl,
            renderStyle: l.renderStyle,
            attributeFields: l.attributeFields,
          };
        });
        dispatch(
          replaceAllLayers({
            layers: restored,
            activeLayerId: state.activeLayerId ?? restored[0]?.id ?? null,
          })
        );

        if (!hasUsableSavedView) {
          restoreViewTimerRef.current = window.setTimeout(() => {
            mapNavigator.zoomToAllLayers(restored);
            restoreViewTimerRef.current = null;
          }, 250);
        }
      }

      // Apply the persisted view after restoring layers so it remains authoritative.
      if (hasUsableSavedView && center) {
        mapNavigator.getMap()?.getView().cancelAnimations();
        dispatch(
          setMapView({
            center: center as [number, number],
            zoom: state.view!.zoom,
            rotation: savedRotation,
          })
        );
      }

      // Restore drawings (annotations)
      if (Array.isArray(state.drawings)) {
        dispatch(clearAllDrawings());
        state.drawings.forEach((d: Drawing) => {
          try {
            dispatch(addDrawing(d));
          } catch {
            /* skip bad drawing */
          }
        });
      }
      if (state.drawingStyle) dispatch(setDrawingStyle(state.drawingStyle));
      if (state.textStyle) dispatch(setTextStyle(state.textStyle));
      if (typeof state.ui?.isNonTechnicalMode === 'boolean') {
        dispatch(setNonTechnicalMode(state.ui.isNonTechnicalMode));
      }
    },
    [dispatch]
  );

  useEffect(
    () => () => {
      if (restoreViewTimerRef.current !== null) {
        window.clearTimeout(restoreViewTimerRef.current);
      }
    },
    []
  );

  const loadList = useCallback(async () => {
    setError(null);
    if (!canSaveToCloud() || !isAuthenticated) {
      setError('Sign in with Google to save and open cloud projects.');
      setProjects([]);
      return;
    }
    try {
      const list = await listCloudProjects();
      setProjects(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load cloud projects');
      setProjects([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (open) {
      loadList();
      const last = getLastCloudProject();
      setProjectName(mode === 'save' && last?.name ? last.name : '');
      setSelectedId(mode === 'save' && last?.id ? last.id : null);
      setOverwrite(!!(mode === 'save' && last?.id));
      setError(null);
    }
  }, [open, loadList, mode]);

  const goSignInForCloud = () => {
    const redirect = `${window.location.pathname}${window.location.search || ''}`;
    onClose();
    navigate(
      `/login?redirect=${encodeURIComponent(redirect || '/map')}&message=${encodeURIComponent(
        'Sign in free to save your project to the cloud.'
      )}`
    );
  };

  const handleSave = async () => {
    if (!projectName.trim()) {
      setError('Enter a project name');
      return;
    }
    if (!canSaveToCloud() || !isAuthenticated) {
      setError('Sign in with Google to save projects to the cloud.');
      dispatch(
        addNotification({
          type: 'warning',
          message: 'Sign in required to save projects to the cloud.',
        })
      );
      goSignInForCloud();
      return;
    }
    setLoading(true);
    setError(null);
    const snapshot = buildSnapshot();
    try {
      // Don't overwrite a local-only draft as cloud id — create new unless real cloud UUID
      const overwriteId =
        overwrite && selectedId && !selectedId.startsWith('local_') ? selectedId : null;
      const result = await saveProjectToCloud({
        name: projectName.trim(),
        projectState: snapshot,
        projectId: overwriteId,
        allowOfflineFallback: false,
      });
      setLastCloudProject(result.id, result.name);
      const nLayers = snapshot.layers?.length ?? 0;
      const nFeatures =
        snapshot.layers?.reduce((sum, l) => sum + (l.features?.length || 0), 0) ?? 0;
      dispatch(
        addNotification({
          type: result.offline ? 'warning' : 'success',
          message:
            result.message ||
            `Saved to cloud: “${result.name}” · ${nLayers} layers · ${nFeatures} features`,
        })
      );
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Cloud save failed';
      setError(msg);
      dispatch(addNotification({ type: 'error', message: msg }));
      if (/sign in/i.test(msg)) {
        // Keep dialog open with error + offer sign-in below
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async (id: string) => {
    if (!id.startsWith('local_') && (!canSaveToCloud() || !isAuthenticated)) {
      setError('Sign in required to open cloud projects.');
      goSignInForCloud();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { projectState, name } = await loadProjectFromCloud(id);
      applySnapshot(projectState);
      setLastCloudProject(id, name);
      const n = projectState.layers?.length ?? 0;
      dispatch(
        addNotification({
          type: 'success',
          message: `Opened from cloud: “${name}” · ${n} layer(s)`,
        })
      );
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Open failed';
      setError(msg);
      dispatch(addNotification({ type: 'error', message: msg }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this project from the cloud permanently?')) return;
    setLoading(true);
    try {
      await deleteCloudProject(id);
      const last = getLastCloudProject();
      if (last?.id === id) {
        try {
          localStorage.removeItem('geovera_last_cloud_project');
        } catch {
          /* ignore */
        }
      }
      await loadList();
      dispatch(addNotification({ type: 'info', message: 'Project deleted from cloud' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const layerCount = layers.length;
  const featureCount = layers.reduce((s, l) => s + (l.features?.length || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'save' ? <CloudUpload color="primary" /> : <CloudDownload color="primary" />}
        {mode === 'save' ? 'Save project to cloud' : 'Open project from cloud'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip size="small" color="primary" label="GeoVera Cloud" sx={{ fontWeight: 700 }} />
          {mode === 'save' && (
            <Typography variant="caption" color="text.secondary">
              {layerCount} layer(s) · {featureCount} feature(s) · basemap {currentBasemap}
              {drawings.length ? ` · ${drawings.length} drawing(s)` : ''}
            </Typography>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
          Your full workspace (map view, basemap, all layers + features + styles, drawings) is
          stored in your GeoVera cloud account — not only on this device.
        </Alert>

        {(!isAuthenticated || !canSaveToCloud()) && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={goSignInForCloud}
                sx={{ fontWeight: 700 }}
              >
                Sign in
              </Button>
            }
          >
            You are not signed in. Cloud save requires a free Google sign-in so your project is
            stored on the server (not only this browser).
          </Alert>
        )}

        {mode === 'save' && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Project name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              autoFocus
              helperText="Saved under your signed-in account"
            />
            {selectedId && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                }
                label="Overwrite selected existing cloud project"
              />
            )}
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {mode === 'save' ? 'Your cloud projects' : 'Open from cloud'}
        </Typography>

        <List
          dense
          sx={{
            maxHeight: 280,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {loading && (
            <ListItem>
              <CircularProgress size={22} sx={{ mr: 1.5 }} />
              <ListItemText primary="Talking to cloud…" />
            </ListItem>
          )}
          {!loading && projects.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No cloud projects yet"
                secondary="Save this workspace to create your first cloud project."
              />
            </ListItem>
          )}
          {projects.map((p) => (
            <ListItemButton
              key={p.id}
              selected={selectedId === p.id}
              onClick={() => {
                setSelectedId(p.id);
                if (mode === 'open') {
                  handleLoad(p.id);
                } else {
                  setProjectName(p.name);
                  setOverwrite(true);
                }
              }}
            >
              <ListItemText
                primary={p.name}
                secondary={p.updatedAt ? new Date(p.updatedAt).toLocaleString() : 'Cloud project'}
              />
              <IconButton
                size="small"
                onClick={(e) => handleDelete(p.id, e)}
                aria-label="Delete from cloud"
              >
                <Delete fontSize="small" />
              </IconButton>
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {mode === 'save' && (
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={loading || !projectName.trim()}
          >
            {overwrite && selectedId ? 'Update cloud project' : 'Save to cloud'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDialog;
